/**
 * StoreProvider
 *
 * React context provider that creates Valtio state and Firebase persistence
 * for multiple named stores. Sets up real-time sync via onSnapshot.
 *
 * State and persist are kept separate in context.
 *
 * @example
 * import { arTableRowsPath } from '#types/System.Artifacts.types';
 * function ArTable({ tableId, parentPath }) {
 *   return (
 *     <StoreProvider
 *       key={`${tableId}:${parentPath}`}
 *       stores={{
 *         schema: { type: 'document', path: `tableTypes/${tableId}` },
 *         rows: {
 *           type: 'collection',
 *           path: arTableRowsPath(parentPath, tableId),
 *           queryOptions: { orderBy: { field: 'createdAt', direction: 'asc' } },
 *         },
 *       }}
 *     >
 *       <ArTableBody />
 *     </StoreProvider>
 *   );
 * }
 */

import { useEffect, useRef, useState, ReactNode } from 'react';
import { createDocumentState } from './state/createDocumentState';
import { createCollectionState } from './state/createCollectionState';
import { persistDocument } from './persist/persistDocument';
import { persistCollection } from './persist/persistCollection';
import { StoresContext } from './core/StoresContext';
import type {
  StoresConfig,
  StoresContextValue,
  CollectionState,
  DocOrCollectionState,
  DocOrCollectionPersist,
} from './Store.types';

type StoreProviderProps = {
  /** Named store configurations */
  stores: StoresConfig;
  /** Children to render */
  children: ReactNode;
};

type StoreEntry = {
  state: DocOrCollectionState;
  persist: DocOrCollectionPersist | null;
  unsubscribe: (() => void) | null;
};

type StoresRegistry = {
  entries: Record<string, StoreEntry>;
  contextValue: StoresContextValue;
};

/**
 * Provides Valtio states and Firebase persists to descendants via context.
 *
 * State is created synchronously during render (safe - pure, no side effects).
 * Subscriptions are created in useEffect (safe - runs after React commits).
 * Children are rendered only after subscriptions are ready.
 */
export function StoreProvider({
  stores,
  children,
}: StoreProviderProps): JSX.Element | null {
  const storeNames = Object.keys(stores);

  if (storeNames.length === 0) {
    throw new Error(
      `[Store] StoreProvider requires at least one store in "stores" prop.`
    );
  }

  for (const name of storeNames) {
    const config = stores[name];
    if (!config.path) {
      throw new Error(
        `[Store] StoreProvider: store "${name}" requires a "path".`
      );
    }
    if (config.type !== 'document' && config.type !== 'collection') {
      throw new Error(
        `[Store] StoreProvider: store "${name}" has invalid type "${config.type}".`
      );
    }
  }

  const [ready, setReady] = useState(false);

  /* Create state synchronously on first render (Valtio pattern: useRef for external stores).
   * State creation is pure - no side effects. Subscriptions happen in useEffect.
   * Intentionally disable ref check, needed for Valtio singleton store pattern. */
  /* eslint-disable react-hooks/refs */
  const registryRef = useRef<StoresRegistry | null>(null);

  if (registryRef.current === null) {
    const entries: Record<string, StoreEntry> = {};
    const states: Record<string, DocOrCollectionState> = {};
    const persists: Record<string, DocOrCollectionPersist> = {};

    for (const name of storeNames) {
      const config = stores[name];

      if (config.type === 'document') {
        entries[name] = {
          state: createDocumentState<Record<string, unknown>>(),
          persist: null,
          unsubscribe: null,
        };
      } else if (config.type === 'collection') {
        entries[name] = {
          state: createCollectionState<Record<string, unknown>>(),
          persist: null,
          unsubscribe: null,
        };
      } else {
        console.error(
          `[Store] StoreProvider: store "${name}" has invalid type "${config.type}".`
        );
        throw new Error(
          `[Store] StoreProvider: store "${name}" has invalid type "${config.type}".`
        );
      }

      states[name] = entries[name].state;
    }

    const contextValue: StoresContextValue = { states, persists };
    registryRef.current = { entries, contextValue };
  }

  const registry = registryRef.current;

  useEffect(() => {
    const storeNames = Object.keys(stores);
    for (const name of storeNames) {
      const config = stores[name];
      const entry = registry.entries[name];

      const previousUnsubscribe = entry.unsubscribe;
      if (previousUnsubscribe) {
        previousUnsubscribe();
        entry.unsubscribe = null;
      }

      (entry.state as any).loading = true;

      if (config.type === 'collection') {
        const collState = entry.state as CollectionState<Record<string, unknown>>;
        collState.docs.clear();
        collState.docIds.length = 0;
      }

      if (config.type === 'document') {
        const { persist, unsubscribe } = persistDocument<Record<string, unknown>>(
          config.path,
          entry.state as ReturnType<typeof createDocumentState>,
          config.onError
        );
        entry.persist = persist;
        entry.unsubscribe = unsubscribe;
      } else {
        const { persist, unsubscribe } = persistCollection<Record<string, unknown>>(
          config.path,
          entry.state as ReturnType<typeof createCollectionState>,
          config.onError,
          config.queryOptions
        );
        entry.persist = persist;
        entry.unsubscribe = unsubscribe;
      }

      registry.contextValue.persists[name] = entry.persist;
    }

    setReady(true);

    return () => {
      for (const name of Object.keys(registry.entries)) {
        const entry = registry.entries[name];
        const unsubscribe = entry.unsubscribe;
        if (unsubscribe) {
          unsubscribe();
          entry.unsubscribe = null;
        }
      }
    };
  }, [registry, stores]);

  if (!ready) {
    return null;
  }

  return (
    <StoresContext.Provider value={registry.contextValue}>
      {children}
    </StoresContext.Provider>
  );
}
